import React, { useState } from "react";
import Input from "../../components/common/input/Input";
import Button from "../../components/common/button/Button";
import Termsandcondition from "../../components/common/t&c/Termsandcondition";
import { useNavigate } from "react-router-dom";
import { useSignupInstance } from "../../utils/userInstance/useSignupIntence";
import CryptoJS from "crypto-js";
import { useSelector } from "react-redux";

const Signup = () => {
  const navigate = useNavigate();

  /* ───────────────── USA Country data ───────────────── */
  const usaCountry = { code: '+1', country: 'US', flag: '🇺🇸', name: 'United States', format: '(xxx) xxx-xxxx', length: 10 };

  /* ───────────────── phone number formatting helper ───────────────── */
  const formatPhoneNumber = (value) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');
    
    // Limit to 10 digits for US
    const limitedDigits = digits.slice(0, 10);
    
    // Apply US formatting: (xxx) xxx-xxxx
    if (limitedDigits.length === 0) return '';
    if (limitedDigits.length <= 3) return `(${limitedDigits}`;
    if (limitedDigits.length <= 6) return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3)}`;
    return `(${limitedDigits.slice(0, 3)}) ${limitedDigits.slice(3, 6)}-${limitedDigits.slice(6)}`;
  };

  /* ─────────────────── state ─────────────────── */
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({
    email: "",
    username: "",
    phoneNumber: "",
    password: "",
    confirmPassword: ""
  });

  const { signup, isErros } = useSignupInstance({});
  const { isUsermanagementLoader } = useSelector(state => state.loader);

  /* ───────────────── password hash helper ───────────────── */
  const generatePasswordHash = (pwd) => {
    const rawKey = "A7VCsRP/mkFmGQrycUixJ26dQ6oft0HymEfocEJjIoI=";
    const keyBytes = CryptoJS.enc.Base64.parse(rawKey);
    const pwdBytes = CryptoJS.enc.Utf8.parse(pwd);
    const combined = pwdBytes.clone().concat(keyBytes);
    const shaHash = CryptoJS.SHA256(combined);
    return CryptoJS.enc.Base64.stringify(shaHash);
  };

  /* ───────────────── navigation ───────────────── */
  const handleLogin = () => navigate("/login");

  /* ───────────────── password visibility toggles ───────────────── */
  const togglePasswordVisibility = () => setShowPassword(!showPassword);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(!showConfirmPassword);

  /* ───────────────── helper to extract email error from API errors ───────────────── */
  const getEmailErrorFromAPI = () => {
    if (!isErros) return "";
    
    if (isErros.email) return isErros.email;
    
    if (isErros.errors && Array.isArray(isErros.errors)) {
      const emailError = isErros.errors.find(error => {
        const errorStr = String(error).toLowerCase();
        return errorStr.includes('email') || 
               errorStr.includes('taken') || 
               errorStr.includes('already') ||
               errorStr.includes('exist');
      });
      
      if (emailError) return emailError;
      if (isErros.errors.length > 0) return isErros.errors[0];
    }
    
    if (isErros.message) {
      const messageStr = String(isErros.message).toLowerCase();
      if (messageStr.includes('email') || 
          messageStr.includes('taken') || 
          messageStr.includes('already') ||
          messageStr.includes('exist') ||
          messageStr.includes('not found')) {
        return isErros.message;
      }
    }
    
    if (isErros.status === 404) return "Email service temporarily unavailable";
    if (isErros.status === 409) return "Email already exists";
    
    return "";
  };

  /* ───────────────── real-time validation helpers ───────────────── */
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!email.trim()) return "Email is required";
    if (!emailRegex.test(email)) return "Please enter a valid email address";
    if (email.length > 254) return "Email is too long";
    return "";
  };

  const validateUsername = (username) => {
    const trimmed = username.trim();
    if (!trimmed) return "Full name is required";
    if (trimmed.length < 1) return "Full name must be at least 1 characters";
    if (trimmed.length > 50) return "Full name must be less than 50 characters";
    if (!/^[a-zA-Z\s]+$/.test(trimmed)) return "Full name must contain only letters and spaces";
    if (/^\s|\s$/.test(username)) return "Full name cannot start or end with spaces";
    if (/\s{2,}/.test(trimmed)) return "Full name cannot contain multiple consecutive spaces";
    return "";
  };

  const validatePhoneNumber = (phone) => {
    const digits = phone.replace(/\D/g, '');
    if (!digits) return "Phone number is required";
    if (digits.length > 10) return "Only 10 numbers allowed";
    if (digits.length < 10) return "Phone number must be exactly 10 digits";
    return "";
  };

  const validatePassword = (password) => {
    if (!password) return "Password is required";
    if (password.length < 8) return "Password must be at least 8 characters long";
    if (password.length > 128) return "Password must be less than 128 characters";
    if (!/(?=.*[a-z])/.test(password)) return "Password must contain at least one lowercase letter";
    if (!/(?=.*[A-Z])/.test(password)) return "Password must contain at least one uppercase letter";
    if (!/(?=.*\d)/.test(password)) return "Password must contain at least one number";
    if (!/(?=.*[@$!%*?&])/.test(password)) return "Password must contain at least one special character (@$!%*?&)";
    if (/\s/.test(password)) return "Password cannot contain spaces";
    
    const commonPatterns = [
      /(.)\1{2,}/,
      /123456|654321|abcdef|qwerty|password/i,
    ];
    
    for (let pattern of commonPatterns) {
      if (pattern.test(password)) {
        return "Password contains common patterns. Please choose a stronger password";
      }
    }
    
    return "";
  };

  const validateConfirmPassword = (confirmPassword, password) => {
    if (!confirmPassword) return "Please confirm your password";
    if (confirmPassword !== password) return "Passwords do not match";
    return "";
  };

  /* ───────────────── input change ───────────────── */
  const handleInputChange = (e, field) => {
    let { value } = e.target;

    if (field === "phoneNumber") {
      const formattedPhone = formatPhoneNumber(value);
      
      if (value !== formattedPhone) {
        e.target.value = formattedPhone;
      }
      
      setPhoneNumber(formattedPhone);
      
      setErrors({
        ...errors,
        phoneNumber: validatePhoneNumber(formattedPhone)
      });
      return;
    }

    if (field === "email") {
      setEmail(value);
      setErrors({ ...errors, email: validateEmail(value) });
    }
    if (field === "username") {
      setUsername(value);
      setErrors({ ...errors, username: validateUsername(value) });
    }
    if (field === "password") {
      setPassword(value);
      const passwordError = validatePassword(value);
      const confirmPasswordError = confirmPassword ? validateConfirmPassword(confirmPassword, value) : "";
      setErrors({ 
        ...errors, 
        password: passwordError,
        confirmPassword: confirmPasswordError
      });
    }
    if (field === "confirmPassword") {
      setConfirmPassword(value);
      setErrors({ 
        ...errors, 
        confirmPassword: validateConfirmPassword(value, password) 
      });
    }
  };

  /* ───────────────── form validation ───────────────── */
  const validateForm = () => {
    const formErr = {
      email: "",
      username: "",
      phoneNumber: "",
      password: "",
      confirmPassword: ""
    };

    const trimmedEmail = email.trim();
    const trimmedUsername = username.trim();

    formErr.email = validateEmail(trimmedEmail);
    formErr.username = validateUsername(trimmedUsername);
    formErr.phoneNumber = validatePhoneNumber(phoneNumber);
    formErr.password = validatePassword(password);
    formErr.confirmPassword = validateConfirmPassword(confirmPassword, password);

    if (!formErr.email && !formErr.username) {
      if (trimmedEmail.toLowerCase() === trimmedUsername.toLowerCase()) {
        formErr.username = "Full name should not be the same as email";
      }
    }

    setErrors(formErr);
    return Object.values(formErr).every(error => error === "");
  };

  /* ───────────────── submit ───────────────── */
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      const encryptedPassword = generatePasswordHash(password);
      const phoneDigits = phoneNumber.replace(/\D/g, '');
      
      const formData = {
        email: email.trim().toLowerCase(),
        name: username.trim(),
        mobile: `${usaCountry.code}${phoneDigits}`, // Include US country code
        password: encryptedPassword
      };
      
      signup(formData);
    }
  };

  /* ───────────────── JSX ───────────────── */
  return (
    <div className="bg-grey-200 h-screen w-full lg:w-4/4 flex flex-col justify-center items-center p-5 login-page">
      <div className="w-full min-w-[200px] xs:max-w-[320px] sm:max-w-[420px]">
        <div>
          <p className="text-2xl font-semibold text-center lg:text-start">
            Let's get started!
          </p>
          <p className="text-sm font-medium mt-2 text-center lg:text-start text-gray-500">
            Already have an account?{" "}
            <span
              className="text-primary underline cursor-pointer"
              onClick={handleLogin}
            >
              Login
            </span>
          </p>
        </div>

        {/* ERROR ALERT */}
        {isErros && (isErros.message || isErros.errors) && (
          <div className="mt-4 p-3 bg-red-100 border border-red-400 rounded-md">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div className="text-red-700">
                {isErros.message && <p className="font-medium">{isErros.message}</p>}
                {isErros.errors && Array.isArray(isErros.errors) && (
                  <ul className="mt-1">
                    {isErros.errors.map((error, index) => (
                      <li key={index} className="text-sm">• {error}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-5">
          {/* FULL NAME */}
          <div className="mt-3">
            <Input
              type="text"
              placeholder="Enter your full name"
              required
              label="Full Name"
              value={username}
              onChange={(e) => handleInputChange(e, "username")}
              onBlur={(e) => {
                const trimmed = e.target.value.trim();
                if (trimmed !== username) {
                  setUsername(trimmed);
                  setErrors({ ...errors, username: validateUsername(trimmed) });
                }
              }}
              onKeyPress={(e) => {
                if (!/[a-zA-Z\s]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              error={errors.username || isErros?.name}
              fullWidth
              maxLength={50}
            />
          </div>

          {/* EMAIL */}
          <div className="mt-3">
            <Input
              type="email"
              placeholder="Enter your email"
              required
              label="Email"
              value={email}
              onChange={(e) => handleInputChange(e, "email")}
              onBlur={(e) => {
                const trimmed = e.target.value.trim().toLowerCase();
                if (trimmed !== email) {
                  setEmail(trimmed);
                  setErrors({ ...errors, email: validateEmail(trimmed) });
                }
              }}
              error={errors.email || getEmailErrorFromAPI()}
              fullWidth
              maxLength={254}
            />
          </div>

          {/* PHONE WITH USA COUNTRY CODE */}
          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone number <span className="text-red-500">*</span>
            </label>
            <div className="relative flex">
              {/* USA Country Display */}
              <div className="h-[41px] px-2 py-1 border border-r-0 border-gray-300 bg-gray-50 flex items-center space-x-2">
                <span className="text-lg">{usaCountry.flag}</span>
                <span className="text-sm font-medium text-gray-700">{usaCountry.code}</span>
              </div>

              {/* Phone Input */}
              <div className="flex-1">
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder={usaCountry.format}
                  required
                  value={phoneNumber}
                  onChange={(e) => {
                    const formatted = formatPhoneNumber(e.target.value);
                    e.target.value = formatted;
                    handleInputChange(e, "phoneNumber");
                  }}
                  onKeyDown={(e) => {
                    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'];
                    if (!allowedKeys.includes(e.key) && !/[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                    
                    const currentDigits = e.target.value.replace(/\D/g, '');
                    if (currentDigits.length >= 10 && /[0-9]/.test(e.key)) {
                      e.preventDefault();
                    }
                  }}
                  onPaste={(e) => {
                    e.preventDefault();
                    const pastedText = e.clipboardData.getData('text');
                    const formatted = formatPhoneNumber(pastedText);
                    setPhoneNumber(formatted);
                    setErrors({
                      ...errors,
                      phoneNumber: validatePhoneNumber(formatted)
                    });
                  }}
                  onDrop={(e) => e.preventDefault()}
                  error={errors.phoneNumber || isErros?.mobile}
                  fullWidth
                  customClassName="rounded-l-none border-l-0"
                />
              </div>
            </div>
          </div>

          {/* PASSWORD */}
          <div className="mt-3 relative">
            <Input
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              required
              label="Password"
              value={password}
              onChange={(e) => handleInputChange(e, "password")}
              error={errors.password || isErros?.password}
              fullWidth
            />
            <button
              type="button"
              className="absolute right-3 top-9 text-gray-500 hover:text-gray-700 focus:outline-none"
              onClick={togglePasswordVisibility}
            >
              {showPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>

          {/* CONFIRM PASSWORD */}
          <div className="mt-3 relative">
            <Input
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm your password"
              required
              label="Confirm Password"
              value={confirmPassword}
              onChange={(e) => handleInputChange(e, "confirmPassword")}
              error={errors.confirmPassword}
              fullWidth
            />
            <button
              type="button"
              className="absolute right-3 top-9 text-gray-500 hover:text-gray-700 focus:outline-none"
              onClick={toggleConfirmPasswordVisibility}
            >
              {showConfirmPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
              )}
            </button>
          </div>

          <p className="text-xs text-red-500 mt-1">
             Password must be at least 8 characters long and include at least one uppercase letter, one lowercase letter, one number, and one special character.
          </p>

          {/* SUBMIT */}
          <div className="mt-5">
            <Button
              label={
                isUsermanagementLoader ? (
                  <div className="flex justify-center">
                    <svg
                      className="animate-spin h-6 w-6 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  </div>
                ) : (
                  "Sign up"
                )
              }
              varient="contained"
              color="primary"
              fullWidth
              onClick={handleSubmit}
              disabled={isUsermanagementLoader}
            />
          </div>

          <Termsandcondition />
        </form>
      </div>
    </div>
  );
};

export default Signup;